import React, { useEffect, useRef, useState } from "react";
import { motion, useMotionValueEvent, useReducedMotion, useScroll } from "framer-motion";
import { ArrowDown, ArrowRight } from "lucide-react";
import BrowserFrame from "./BrowserFrame";
import SiteShot from "./SiteShot";
import { EASE } from "./Reveal";
import useIsDesktop from "./useIsDesktop";

/**
 * The page's signature moment: scroll through and a website actually gets built.
 *
 * Stage 0 an empty frame with the domain typed in, stage 1 grey wireframe blocks,
 * stage 2 those blocks take on brand colour and hierarchy, stage 3 the real client
 * site loads and goes live. It is the studio's own process performed on the studio's
 * own page, which is why it can't read as a generic template section.
 */

const WIRE = "#16233A";
const BUILT = "#1E3A5C";
const ACCENT = "#38BDF8";
const SIGNAL = "#FF6A3D";

/** Per-stage look for one wireframe block. */
function block(stage, { delay = 0, built = BUILT, wire = WIRE, radius = 4 }) {
  const visible = stage >= 1;
  return {
    animate: {
      opacity: visible ? 1 : 0,
      y: visible ? 0 : 8,
      backgroundColor: stage >= 2 ? built : wire,
      borderRadius: radius,
    },
    transition: { duration: 0.55, ease: EASE, delay: visible ? delay : 0 },
  };
}

function Wireframe({ stage }) {
  return (
    <div className="absolute inset-0 flex flex-col p-4 sm:p-6">
      {/* Nav */}
      <div className="flex shrink-0 items-center justify-between">
        <motion.div className="h-4 w-20 sm:h-5 sm:w-24" {...block(stage, { delay: 0, radius: 3 })} />
        <div className="hidden items-center gap-3 sm:flex">
          {[0, 1, 2].map((i) => (
            <motion.div
              key={i}
              className="h-2.5 w-12"
              {...block(stage, { delay: 0.06 + i * 0.04, radius: 2 })}
            />
          ))}
        </div>
        <motion.div
          className="h-6 w-16 sm:h-7 sm:w-24"
          {...block(stage, { delay: 0.2, built: SIGNAL, radius: 999 })}
        />
      </div>

      {/* Hero */}
      <div className="mt-6 grid flex-1 grid-cols-1 gap-5 sm:mt-10 sm:grid-cols-[1.1fr_1fr] sm:gap-8">
        <div className="flex flex-col justify-center gap-3">
          <motion.div
            className="h-6 w-[85%] sm:h-9"
            {...block(stage, { delay: 0.26, radius: 4 })}
          />
          <motion.div
            className="h-6 w-[60%] sm:h-9"
            {...block(stage, { delay: 0.32, built: ACCENT, radius: 4 })}
          />
          <div className="mt-2 flex flex-col gap-2">
            <motion.div className="h-2 w-[92%]" {...block(stage, { delay: 0.38, radius: 2 })} />
            <motion.div className="h-2 w-[78%]" {...block(stage, { delay: 0.42, radius: 2 })} />
          </div>
          <motion.div
            className="mt-3 h-8 w-32 sm:h-10 sm:w-40"
            {...block(stage, { delay: 0.48, built: SIGNAL, radius: 999 })}
          />
        </div>
        <motion.div
          className="min-h-[80px] flex-1"
          {...block(stage, { delay: 0.34, radius: 8 })}
        />
      </div>

      {/* Cards */}
      <div className="mt-5 grid shrink-0 grid-cols-3 gap-3 sm:mt-8 sm:gap-5">
        {[0, 1, 2].map((i) => (
          <motion.div
            key={i}
            className="h-12 sm:h-20"
            {...block(stage, { delay: 0.54 + i * 0.06, radius: 8 })}
          />
        ))}
      </div>
    </div>
  );
}

export function BuildSequence({ site, steps, heading }) {
  const reduce = useReducedMotion();
  const isDesktop = useIsDesktop();
  // The page's single pinned scroll moment. Anything narrower than desktop, or a
  // reduced-motion preference, gets the same content as a plain stacked block.
  const pinned = isDesktop && !reduce;

  const containerRef = useRef(null);
  const [stage, setStage] = useState(0);
  const [phase, setPhase] = useState("typing");

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end end"],
  });

  useMotionValueEvent(scrollYProgress, "change", (p) => {
    if (!pinned) return;
    setStage(Math.min(steps.length - 1, Math.max(0, Math.floor(p * steps.length))));
  });

  // Run a load sweep on the way into the final stage, then go live.
  useEffect(() => {
    const last = steps.length - 1;
    if (stage < last) {
      setPhase("typing");
      return undefined;
    }
    setPhase("loading");
    const id = setTimeout(() => setPhase("loaded"), 620);
    return () => clearTimeout(id);
  }, [stage, steps.length]);

  const isLive = stage >= steps.length - 1;

  /** Scroll to the middle of a given stage's slice of the pinned range. */
  const goToStage = (i) => {
    if (!pinned || !containerRef.current) {
      setStage(i);
      return;
    }
    const el = containerRef.current;
    const travel = el.offsetHeight - window.innerHeight;
    const top = el.getBoundingClientRect().top + window.scrollY;
    window.scrollTo({ top: top + travel * ((i + 0.5) / steps.length), behavior: "smooth" });
  };

  // Scrolling is the primary way through, but not everyone scrolls — this drives
  // the same sequence by click, then hands off to pricing at the end.
  const advance = (
    <div className="mt-5 flex shrink-0 items-center justify-center">
      {isLive ? (
        <a
          href="/appointment"
          className="dp-focus group inline-flex items-center gap-2 rounded-full bg-[#FF6A3D] px-6 py-3 text-[14px] font-semibold text-white transition-colors duration-300 hover:bg-[#FF8055]"
        >
          Book a call
          <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1" />
        </a>
      ) : (
        <button
          type="button"
          onClick={() => goToStage(stage + 1)}
          className="dp-focus group inline-flex items-center gap-2 rounded-full border border-[#25395A] px-6 py-3 text-[13.5px] font-medium text-[#EDF1F7] transition-colors duration-300 hover:border-[#3D5680] hover:bg-[#0E1929]"
        >
          Next: {steps[stage + 1].title}
          <ArrowDown className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-y-0.5" />
        </button>
      )}
    </div>
  );

  const stageList = (
    <ol className="space-y-1">
      {steps.map((step, i) => {
        const active = stage === i;
        const done = stage > i;
        return (
          <li key={step.step}>
            <button type="button" onClick={() => goToStage(i)}
              className="dp-focus block w-full border-l-2 py-3 pl-4 text-left transition-colors duration-500"
              style={{ borderColor: active ? SIGNAL : done ? "#2F4770" : "#16233A" }}
              aria-current={active ? "step" : undefined}
            >
              <div className="flex items-center gap-2.5">
                <span
                  className="dp-mono text-[11px] tracking-[0.18em] transition-colors duration-500"
                  style={{ color: active ? SIGNAL : "#3A4E6E" }}
                >
                  {step.step}
                </span>
                <span
                  className="dp-mono text-[9.5px] uppercase tracking-[0.14em] transition-colors duration-500"
                  style={{ color: active ? "#B6C6DE" : "#2F4770" }}
                >
                  {step.yours ? "you're in" : "we've got it"}
                </span>
              </div>
              <h3
                className="dp-display mt-1.5 text-[19px] font-semibold tracking-[-0.02em] transition-colors duration-500 sm:text-[22px]"
                style={{ color: active ? "#FFFFFF" : done ? "#7E93B2" : "#3E5478" }}
              >
                {step.title}
              </h3>
              {/* Space is RESERVED rather than animated. Animating height here
                  resizes content mid-scroll, which trips Chrome's scroll anchoring
                  and cancels any in-flight smooth scroll (so #packages / #faq
                  anchor jumps would stop short). Opacity only: no layout change. */}
              <motion.p
                className="mt-2 h-[2.9rem] text-[14px] leading-relaxed text-[#7E93B2]"
                initial={false}
                animate={{ opacity: active ? 1 : 0 }}
                transition={{ duration: 0.35, ease: EASE }}
                aria-hidden={!active}
              >
                {step.description}
              </motion.p>
            </button>
          </li>
        );
      })}
    </ol>
  );

  const frame = (
    <BrowserFrame
      url={`https://${site.domain}`}
      caret={stage === 0 && !reduce}
      phase={reduce ? "loaded" : phase}
      fill
      className="min-h-0 flex-1"
    >
      <div className="relative h-full w-full overflow-hidden">
        {/* Blank canvas at stage 0: an empty browser reads as a void, a ruled
            canvas reads as "nothing here yet, ready to start". */}
        <motion.div
          className="absolute inset-0"
          initial={false}
          animate={{ opacity: stage === 0 ? 1 : 0 }}
          transition={{ duration: 0.5, ease: EASE }}
          aria-hidden="true"
        >
          <div
            className="absolute inset-0"
            style={{
              backgroundImage:
                "linear-gradient(to right, rgba(56,189,248,0.055) 1px, transparent 1px), linear-gradient(to bottom, rgba(56,189,248,0.055) 1px, transparent 1px)",
              backgroundSize: "40px 40px",
            }}
          />
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="dp-mono text-[10px] uppercase tracking-[0.24em] text-[#2A3D5C]">
              blank canvas
            </span>
          </div>
        </motion.div>

        {/* Wireframe, which becomes a designed layout, then hands over to the real site */}
        <motion.div
          className="absolute inset-0"
          animate={{ opacity: isLive ? 0 : 1 }}
          transition={{ duration: 0.5, ease: EASE }}
        >
          <Wireframe stage={stage} />
        </motion.div>

        <SiteShot
          image={site.image}
          alt={`${site.name} website, built by DataPulse Web`}
          className="absolute inset-0 h-full w-full object-cover object-top"
          initial={false}
          animate={{ opacity: isLive && phase === "loaded" ? 1 : 0 }}
          transition={{ duration: 0.6, ease: EASE }}
        />
      </div>
    </BrowserFrame>
  );

  // No pinning: same four stages, stepped through by tapping instead of scrolling.
  if (!pinned) {
    return (
      <div className="px-5 py-16 sm:px-8 sm:py-24">
        <div className="mx-auto max-w-6xl">
          {heading}
          <div className="mt-10 flex h-[15rem] flex-col sm:h-[22rem]">{frame}</div>
          {advance}
          <div className="mt-8">{stageList}</div>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      // Belt and braces alongside the fixed-height descriptions above: keep Chrome
      // from re-anchoring scroll inside this range.
      style={{ height: `${steps.length * 82}vh`, overflowAnchor: "none" }}
    >
      {/* flex-1 on the inner container (not justify-center) is what lets the frame
          actually fill the screen height. */}
      <div className="sticky top-0 flex h-screen flex-col px-5 pb-10 pt-24 sm:px-8">
        <div className="mx-auto flex min-h-0 w-full max-w-6xl flex-1 flex-col">
          <div className="shrink-0">{heading}</div>

          <div className="mt-7 grid min-h-0 flex-1 gap-8 lg:grid-cols-[20rem_minmax(0,1fr)] lg:gap-12">
            <div className="min-h-0">{stageList}</div>

            {/* Hold real browser proportions rather than stretching to fill the
                column, so the wireframe reads as a website and not a tall panel. */}
            <div className="flex min-h-0 items-center">
              <div
                className="flex w-full flex-col"
                style={{ aspectRatio: "16 / 10", maxHeight: "100%" }}
              >
                {frame}
              </div>
            </div>
          </div>

          {advance}
        </div>
      </div>
    </div>
  );
}

export default BuildSequence;
